<template>
  <div class="relative">
    <div
      ref="listContainerRef"
      class="list-container"
      :class="twMerge('flex overflow-y-hidden no-scrollbar lg:overflow-hidden', props.listContainerClass)"
    >
      <div v-for="(item, index) in list" :key="item[props.idKey]" ref="itemRefList" class="item" :class="props.itemClass">
        <slot :item="item" :index="index" />
      </div>
    </div>

    <div
      class="list-arrow list-arrow-left hidden bg-gradient-to-r left-0 justify-left"
      :class="[isLeftArrowShown && 'lg:flex', twMerge('h-full w-8', props.arrowBtnClass, props.arrowLeftBtnClass)]"
    >
      <svg-icon
        name="arrow-left"
        class="cursor-pointer"
        :class="twMerge('size-6 p-1 transition-colors rounded hover:bg-gray-20', props.arrowIconClass, props.arrowIconLeftClass)"
        @click="onArrowClick('left')"
      />
    </div>
    <div
      class="list-arrow list-arrow-right hidden bg-gradient-to-l right-0 justify-end"
      :class="[isRightArrowShown && 'lg:flex', twMerge('h-full w-8', props.arrowBtnClass, props.arrowRightBtnClass)]"
    >
      <svg-icon
        name="arrow-right"
        class="cursor-pointer"
        :class="twMerge('size-6 p-1 transition-colors rounded hover:bg-gray-20', props.arrowIconClass, props.arrowIconRightClass)"
        @click="onArrowClick('right')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends Record<string, any>">
import type { HTMLAttributes } from 'vue'
import { twMerge } from 'tailwind-merge'

import { useAppStore } from '@/store/app'

interface Props {
  list: T[]
  idKey: string
  slidesPerGroup?: number
  listContainerClass?: HTMLAttributes['class']
  itemClass?: HTMLAttributes['class']
  arrowBtnClass?: HTMLAttributes['class']
  arrowIconClass?: HTMLAttributes['class']
  arrowLeftBtnClass?: HTMLAttributes['class']
  arrowIconLeftClass?: HTMLAttributes['class']
  arrowRightBtnClass?: HTMLAttributes['class']
  arrowIconRightClass?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), { slidesPerGroup: 1 })
const { list, slidesPerGroup } = toRefs(props)

const appStore = useAppStore()

const listContainerRef = ref<Nullable<HTMLDivElement>>(null)
const itemRefList = ref<HTMLDivElement[]>([])
const isLeftArrowShown = ref(false)
const isRightArrowShown = ref(false)

const firstItemRef = computed(() => itemRefList.value[0])

const { width: itemWidth } = useElementSize(firstItemRef, undefined, { box: 'border-box' })

// FIXME: 從小畫面 resize 成大畫面時，箭頭不會出現
useIntersectionObserver(
  () => itemRefList.value[0],
  ([entry]) => {
    isLeftArrowShown.value = !entry.isIntersecting
  },
  { root: listContainerRef, threshold: 1, immediate: appStore.isLargeDesktopScreen }
)
useIntersectionObserver(
  () => itemRefList.value[list.value.length - 1],
  ([entry]) => {
    isRightArrowShown.value = !entry.isIntersecting
  },
  { root: listContainerRef, threshold: 1, immediate: appStore.isLargeDesktopScreen }
)

onBeforeUnmount(() => {
  itemRefList.value = []
})

function onArrowClick(type: 'left' | 'right') {
  if (!listContainerRef.value) {
    return
  }

  const moveLeft = itemWidth.value * slidesPerGroup.value * (type === 'left' ? -1 : 1)

  listContainerRef.value.scrollBy({ left: moveLeft, behavior: 'smooth' })
}
</script>

<style lang="scss" scoped>
.list-arrow {
  @apply absolute top-0 items-center from-gray-10 transition-opacity duration-300;
}
</style>
